/**
 * sessionExpirySignal — Issue #289.
 *
 * Single source-of-truth event bus for backend-reported session-idle-timeout
 * rejections in admin-web. Any protected API call that receives a 401 caused
 * by idle-expiration must call `notifySessionIdleExpired()`. `AuthContext`
 * subscribes on mount and reacts by clearing the stored access token and
 * dropping the session — `ProtectedShell` then redirects to `/login`.
 *
 * 红线 (AI GUIDE §6):
 *   - The signal carries only a sanitized reason code (`idle_expired`,
 *     `session_revoked`, ...). It MUST NOT carry tokens, cookies, refresh
 *     tokens, token hashes, bearer tokens, PII, or production env values.
 *   - The detection logic stays defensive: a non-idle 401 (e.g. permission
 *     denied) must NOT signal idle expiration, otherwise users would be
 *     bounced to /login on legitimate 403/401 errors.
 */
export type SessionExpiryReason =
  | 'idle_expired'
  | 'session_revoked'
  | 'session_not_found';

export type SessionExpiryListener = (reason: SessionExpiryReason) => void;

const listeners = new Set<SessionExpiryListener>();

/**
 * Notify all subscribers that the backend has declared the current session
 * idle-expired (or revoked). Subscribers should clear local auth state.
 */
export function notifySessionIdleExpired(reason: SessionExpiryReason = 'idle_expired'): void {
  for (const listener of listeners) {
    try {
      listener(reason);
    } catch {
      // A single broken listener must not prevent the others from running.
    }
  }
}

/**
 * Subscribe to backend-reported session expiration. Returns an unsubscribe
 * function that callers should invoke from `useEffect` cleanup.
 */
export function subscribeToSessionIdleExpired(listener: SessionExpiryListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Test-only: drop all subscribers. Production code must never call this.
 */
export function __resetSessionExpirySignalForTests(): void {
  listeners.clear();
}

/**
 * Shape we look for in 401 error bodies. All fields are optional because the
 * backend may surface the idle-expiry reason via `message`, `error`, or a
 * structured `code`. Using a typed shape lets us narrow without resorting to
 * `as`-casts that the AI GUIDE §6 red-line scan forbids.
 */
export interface AuthErrorBody {
  readonly message?: unknown;
  readonly code?: unknown;
  readonly error?: unknown;
}

/**
 * Safely parse a JSON response body text into an `AuthErrorBody`. Returns
 * `null` for empty bodies, non-JSON bodies, arrays, or primitives — anything
 * that cannot syntactically be the backend's error envelope. The returned
 * value is structurally typed so callers do not need any cast.
 */
export function parseAuthErrorBody(text: string | null | undefined): AuthErrorBody | null {
  if (!text) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  return parsed;
}

/**
 * Decide whether a 401 response body indicates session idle expiration. The
 * backend rejects idle sessions with `message: 'Session idle timeout expired'`
 * (see `TokenService.validateRefreshToken` and `SessionActivityService.touch`).
 * We match on the stable public message text (and on an explicit `code` if
 * later added) rather than any token material — the message surfaces in 401s
 * publicly and carries no secrets.
 */
export function isIdleExpiredResponseBody(payload: AuthErrorBody | null | undefined): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const code = typeof payload.code === 'string' ? payload.code.toLowerCase() : '';
  if (code === 'session_idle_expired') return true;
  const candidates = [payload.message, payload.error]
    .filter((v): v is string => typeof v === 'string')
    .map((v) => v.toLowerCase());
  return candidates.some((s) =>
    s.includes('session idle timeout expired') ||
    s.includes('session not found') ||
    s.includes('session revoked'),
  );
}
